/**
 * JavaValidationAnalysisView
 *
 * Displays the repair + structural validation report for a java_validation job.
 *
 * Shows:
 *   - Overall PASS / FAIL status badge
 *   - Summary stats: files scanned, files modified, total changes, validation pass/warn/fail
 *   - Horizontal bar chart: changes applied per repair rule
 *   - Validation breakdown: files passed / warned / failed
 *   - Failure detail: file list + first error per failing file
 *   - Mode indicator (strict / permissive)
 *
 * Customer-facing wording only — no internal terms like "AST".
 *
 * Data source: GET /java-validation/results/json/{jobId}/{filename}
 */

import { useState, useEffect, useCallback } from 'react'
import {
  CheckCircle2, XCircle, AlertTriangle, FileCode2,
  Wrench, ShieldCheck, Info, ChevronDown, ChevronRight,
  ArrowLeft, Loader2,
} from 'lucide-react'
import { getAPIBaseUrl } from '../../environment'
import './JavaValidationAnalysisView.css'

// ── Rule code → customer-friendly label ──────────────────────────────────────
const RULE_LABELS = {
  // Validation rules
  EMPTY_FILE:                       'Empty File',
  MISSING_PACKAGE:                  'Missing Package Declaration',
  MISSING_TYPE_DECL:                'Missing Class / Interface Declaration',
  TODO_MARKERS:                     'TODO Markers Found',
  VERY_FEW_LINES:                   'File Too Short',
  IMPORT_REFERENCES_MISSING_TYPE:   'Unresolved Import Reference',
  UNRESOLVED_FIELD_TYPE:            'Unresolved Field Type',
  UNRESOLVED_SUPERTYPE:             'Unresolved Parent Class',
  UNRESOLVED_INTERFACE:             'Unresolved Interface',
  READ_ERROR:                       'File Read Error',
  // Repair rules (from java_repair engine)
  STRING_LITERAL_QUOTE_FIX:         'String Literal Quote Fix',
  MISSING_SEMICOLON:                'Missing Semicolon',
  UNCLOSED_BRACE:                   'Unclosed Brace',
  INVALID_CHAR:                     'Invalid Character Removal',
  ORPHAN_STATEMENT:                 'Orphaned Statement Fix',
}

function ruleLabel(code) {
  if (RULE_LABELS[code]) return RULE_LABELS[code]
  // Generic: SCREAMING_SNAKE → Title Case
  return code
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase())
}

// ── Auth helper ───────────────────────────────────────────────────────────────
function authHeaders() {
  const token = localStorage.getItem('modernizeit_token')
  const h = { 'Content-Type': 'application/json' }
  if (token) h['Authorization'] = `Bearer ${token}`
  return h
}

// ── Sub-components ────────────────────────────────────────────────────────────

function StatCard({ value, label, icon: Icon, variant = 'neutral' }) {
  return (
    <div className={`jval-stat-card jval-stat-card--${variant}`}>
      {Icon && <Icon size={20} className="jval-stat-icon" />}
      <span className="jval-stat-value">{value ?? '—'}</span>
      <span className="jval-stat-label">{label}</span>
    </div>
  )
}

function RuleBar({ rule, count, max }) {
  const pct = max > 0 ? Math.round((count / max) * 100) : 0
  return (
    <div className="jval-rule-row">
      <span className="jval-rule-label" title={rule}>{ruleLabel(rule)}</span>
      <div className="jval-rule-bar-wrap">
        <div className="jval-rule-bar" style={{ width: `${pct}%` }} />
      </div>
      <span className="jval-rule-count">{count}</span>
    </div>
  )
}

function FailingFileRow({ file }) {
  const [open, setOpen] = useState(false)
  const errors = file.findings?.filter(f => f.severity === 'ERROR') || []
  const warnings = file.findings?.filter(f => f.severity === 'WARNING') || []

  return (
    <div className="jval-file-row">
      <button
        className="jval-file-toggle"
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
      >
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <FileCode2 size={14} className="jval-file-icon" />
        <span className="jval-file-path">{file.path}</span>
        {errors.length > 0 && (
          <span className="jval-badge jval-badge--error">{errors.length} error{errors.length !== 1 ? 's' : ''}</span>
        )}
        {warnings.length > 0 && (
          <span className="jval-badge jval-badge--warn">{warnings.length} warning{warnings.length !== 1 ? 's' : ''}</span>
        )}
      </button>
      {open && (
        <ul className="jval-findings-list">
          {file.findings?.map((f, i) => (
            <li key={i} className={`jval-finding jval-finding--${f.severity?.toLowerCase()}`}>
              <span className="jval-finding-code">{ruleLabel(f.code)}</span>
              <span className="jval-finding-msg">{f.message}</span>
              {f.cobol_line != null && (
                <span className="jval-cobol-loc" title="Original COBOL source location">
                  COBOL: {f.cobol_file ? `${f.cobol_file}:${f.cobol_line}` : `line ${f.cobol_line}`}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

// ── Main component ────────────────────────────────────────────────────────────

export default function JavaValidationAnalysisView({ analysisContext, onBack, onClose }) {
  const [repairData, setRepairData]       = useState(null)
  const [validationData, setValidationData] = useState(null)
  const [loading, setLoading]             = useState(true)
  const [error, setError]                 = useState(null)

  const handleClose = onBack || onClose
  const jobId = analysisContext?.jobId || analysisContext?.jobs?.java_validation_job_id
  const apiBase = getAPIBaseUrl()

  const fetchReports = useCallback(async () => {
    if (!jobId) {
      setError('No job ID available. Re-run the node to generate reports.')
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const [repairRes, valRes] = await Promise.all([
        fetch(`${apiBase}/java-validation/results/json/${jobId}/java_repair_report.json`, { headers: authHeaders() }),
        fetch(`${apiBase}/java-validation/results/json/${jobId}/java_validation_report.json`, { headers: authHeaders() }),
      ])

      if (!repairRes.ok && !valRes.ok) {
        throw new Error(`Reports not found (${repairRes.status}). The job may not have produced artifacts yet.`)
      }

      const [repair, validation] = await Promise.all([
        repairRes.ok  ? repairRes.json()  : Promise.resolve(null),
        valRes.ok     ? valRes.json()     : Promise.resolve(null),
      ])

      setRepairData(repair)
      setValidationData(validation)
    } catch (err) {
      setError(err.message || 'Failed to load reports')
    } finally {
      setLoading(false)
    }
  }, [jobId, apiBase])

  useEffect(() => { fetchReports() }, [fetchReports])

  // ── Derived data ────────────────────────────────────────────────────────────
  const overall   = validationData?.overall_status   // 'PASSED' | 'WARNINGS' | 'FAILED'
  const summary   = validationData?.summary          || {}
  const valFiles  = validationData?.files            || []
  const strictMode = validationData?.strict_mode

  const repairByRule   = repairData?.changes_by_rule || {}
  const repairFiles    = repairData?.files           || []
  const ruleEntries    = Object.entries(repairByRule).sort(([, a], [, b]) => b - a)
  const maxRuleCount   = ruleEntries.length > 0 ? ruleEntries[0][1] : 0

  const failingFiles   = valFiles.filter(f => f.status === 'FAILED')
  const warningFiles   = valFiles.filter(f => f.status === 'WARNINGS')

  const passed = overall === 'PASSED' || (overall === 'WARNINGS' && summary.error_count === 0)

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="jval-view">
      {/* Header */}
      <div className="jval-header">
        <div className="jval-header-left">
          {handleClose && (
            <button className="jval-back-btn" onClick={handleClose} title="Back to workflow">
              <ArrowLeft size={16} />
            </button>
          )}
          <ShieldCheck size={20} className="jval-header-icon" />
          <div>
            <h2 className="jval-title">Validation Report</h2>
            <p className="jval-subtitle">
              Java repair + structural validation
              {strictMode !== undefined && (
                <span className={`jval-mode-badge ${strictMode ? 'jval-mode-badge--strict' : ''}`}>
                  {strictMode ? 'strict mode' : 'permissive mode'}
                </span>
              )}
            </p>
          </div>
        </div>
        {!loading && overall && (
          <div className={`jval-status-badge ${passed ? 'jval-status-badge--pass' : overall === 'WARNINGS' ? 'jval-status-badge--warn' : 'jval-status-badge--fail'}`}>
            {passed
              ? <><CheckCircle2 size={18} /> PASSED</>
              : overall === 'WARNINGS'
                ? <><AlertTriangle size={18} /> WARNINGS</>
                : <><XCircle size={18} /> FAILED</>
            }
          </div>
        )}
      </div>

      {/* Body */}
      <div className="jval-body">
        {loading && (
          <div className="jval-loading">
            <Loader2 size={24} className="jval-spin" />
            <span>Loading reports…</span>
          </div>
        )}

        {error && (
          <div className="jval-error">
            <XCircle size={18} />
            <span>{error}</span>
          </div>
        )}

        {!loading && !error && (
          <>
            {/* ── Summary stats ────────────────────────────────────────── */}
            <section className="jval-section">
              <div className="jval-stat-grid">
                <StatCard
                  value={summary.files_checked}
                  label="Files Checked"
                  icon={FileCode2}
                  variant="neutral"
                />
                <StatCard
                  value={repairData?.files_modified ?? '—'}
                  label="Files Repaired"
                  icon={Wrench}
                  variant={repairData?.files_modified > 0 ? 'info' : 'neutral'}
                />
                <StatCard
                  value={repairData?.changes_total ?? '—'}
                  label="Repairs Applied"
                  icon={Wrench}
                  variant={repairData?.changes_total > 0 ? 'info' : 'neutral'}
                />
                <StatCard
                  value={summary.files_passed}
                  label="Files Passed"
                  icon={CheckCircle2}
                  variant="pass"
                />
                {summary.files_warned > 0 && (
                  <StatCard
                    value={summary.files_warned}
                    label="Files with Warnings"
                    icon={AlertTriangle}
                    variant="warn"
                  />
                )}
                {summary.files_failed > 0 && (
                  <StatCard
                    value={summary.files_failed}
                    label="Files Failed"
                    icon={XCircle}
                    variant="fail"
                  />
                )}
              </div>
            </section>

            {/* ── Repair chart ─────────────────────────────────────────── */}
            {ruleEntries.length > 0 && (
              <section className="jval-section">
                <h3 className="jval-section-title">
                  <Wrench size={15} /> Repairs Applied by Rule
                </h3>
                <div className="jval-chart">
                  {ruleEntries.map(([rule, count]) => (
                    <RuleBar key={rule} rule={rule} count={count} max={maxRuleCount} />
                  ))}
                </div>
              </section>
            )}

            {repairData && repairData.changes_total === 0 && (
              <section className="jval-section jval-info-row">
                <Info size={14} />
                <span>No repairs were needed — all files were already well-formed.</span>
              </section>
            )}

            {/* ── Validation errors ─────────────────────────────────────── */}
            {failingFiles.length > 0 && (
              <section className="jval-section">
                <h3 className="jval-section-title jval-section-title--fail">
                  <XCircle size={15} /> Validation Errors ({failingFiles.length} file{failingFiles.length !== 1 ? 's' : ''})
                </h3>
                <div className="jval-files-list">
                  {failingFiles.map(f => (
                    <FailingFileRow key={f.path} file={f} />
                  ))}
                </div>
              </section>
            )}

            {/* ── Warnings ─────────────────────────────────────────────── */}
            {warningFiles.length > 0 && (
              <section className="jval-section">
                <h3 className="jval-section-title jval-section-title--warn">
                  <AlertTriangle size={15} /> Warnings ({warningFiles.length} file{warningFiles.length !== 1 ? 's' : ''})
                </h3>
                <div className="jval-files-list">
                  {warningFiles.map(f => (
                    <FailingFileRow key={f.path} file={f} />
                  ))}
                </div>
              </section>
            )}

            {/* ── All-clear ─────────────────────────────────────────────── */}
            {passed && failingFiles.length === 0 && warningFiles.length === 0 && validationData && (
              <section className="jval-section jval-all-clear">
                <CheckCircle2 size={32} className="jval-all-clear-icon" />
                <p>All {summary.files_checked} files passed structural validation.</p>
                <p className="jval-all-clear-sub">
                  Java Packaging can proceed safely.
                </p>
              </section>
            )}
          </>
        )}
      </div>
    </div>
  )
}
